
import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useInfiniteQuery } from '@tanstack/react-query';
import ProductCard, { ProductCardProps } from '@/components/product/card/ProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { PackageIcon, ShoppingBag } from 'lucide-react';

interface UserProductListProps {
  userId: string;
  isStore?: boolean;
  storeName?: string;
  username?: string;
  fullName?: string;
}

const PAGE_SIZE = 10;

const fetchUserProducts = async ({ pageParam = 0, userId }: { pageParam: number; userId: string }) => {
  const from = pageParam * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  const { data, error, count } = await supabase
    .from('listings')
    .select('*, category:categories(name), subcategory:subcategories(name)', { count: 'exact' })
    .eq('user_id', userId)
    .eq('is_active', true)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) throw error;

  const products = (data || []).map(listing => ({
    ...listing,
    id: String(listing.id),
    imageUrl: listing.images && listing.images.length > 0 ? listing.images[0] : '/placeholder.svg',
    category: listing.category?.name || 'E panjohur',
    subcategory: listing.subcategory?.name,
    createdAt: listing.created_at,
    price: listing.price ?? 0,
    location: listing.location ?? 'N/A',
  })) as ProductCardProps[];
  
  return { data: products, nextPage: products.length === PAGE_SIZE ? pageParam + 1 : undefined, count };
};

const UserProductList: React.FC<UserProductListProps> = ({ userId, isStore, storeName, username, fullName }) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ['userProducts', userId],
    queryFn: ({ pageParam }) => fetchUserProducts({ pageParam: pageParam as number, userId }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

  const displayName = isStore ? storeName : (username || fullName || 'ky përdorues');
  const allProducts = data?.pages.flatMap(page => page.data) || [];
  const totalProducts = data?.pages[0]?.count ?? 0;


  if (isLoading && !data) {
    return (
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-6">
          <PackageIcon size={24} className="text-gray-700 dark:text-gray-300" />
          <h3 className="text-xl font-bold">
            Produkte nga {displayName}
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="rounded-xl border bg-card text-card-foreground shadow h-[400px]">
              <div className="p-4 space-y-3">
                <Skeleton className="h-[150px] w-full rounded-md" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="mt-8 text-red-500">Gabim gjatë ngarkimit të produkteve: {(error as Error).message}</div>;
  }

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-6">
        <PackageIcon size={24} className="text-gray-700 dark:text-gray-300" />
        <h3 className="text-xl font-bold">
          Produkte nga {displayName}
          {totalProducts > 0 && ` (${totalProducts})`}
        </h3>
      </div>

      {allProducts.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allProducts.map((product) => (
              <ProductCard key={product.id} {...product} />
            ))}
          </div>
          {hasNextPage && (
            <div className="mt-8 text-center">
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Duke ngarkuar më shumë...' : 'Ngarko më shumë'}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          <ShoppingBag size={48} className="mx-auto mb-4 opacity-50" />
          <p>Ky {isStore ? 'dyqan' : 'përdorues'} nuk ka produkte aktive për momentin.</p>
        </div>
      )}
    </div>
  );
};

export default UserProductList;
